/**
 * Cart page: quantity changes and removals via /cart/change.js, then the
 * whole section re-renders through the Section Rendering API. Coupon apply
 * is delegated to the shared WsCoupon helper (ws-cart.js).
 */
window.WsCartPage = class WsCartPage extends HTMLElement {
  #abort = null;
  #controller = null;

  connectedCallback() {
    this.#abort = new AbortController();
    const { signal } = this.#abort;

    this.addEventListener('click', (event) => {
      const stepper = event.target.closest('[data-cart-qty-change]');
      if (stepper) {
        const input = this.querySelector(`[data-cart-qty][data-line="${stepper.dataset.line}"]`);
        const quantity = Math.max(0, Number(input?.value ?? 0) + Number(stepper.dataset.cartQtyChange));
        this.#changeLine(stepper.dataset.line, quantity);
        return;
      }

      const remove = event.target.closest('[data-cart-remove]');
      if (remove) {
        this.#changeLine(remove.dataset.line, 0);
        return;
      }

      if (event.target.closest('[data-coupon-apply]')) {
        window.WsCoupon?.apply(this);
        return;
      }

      const couponRemove = event.target.closest('[data-coupon-remove]');
      if (couponRemove) {
        window.WsCoupon?.remove(this, couponRemove.dataset.couponRemove);
      }
    }, { signal });

    this.addEventListener('change', (event) => {
      const input = event.target.closest('[data-cart-qty]');
      if (input) this.#changeLine(input.dataset.line, Math.max(0, Number(input.value)));
    }, { signal });

    // Coupon apply/remove (and other cart mutations) broadcast cart:refresh —
    // re-render the page so totals, discount rows and chips stay in sync.
    document.addEventListener('cart:refresh', () => this.#rerender(), { signal });
  }

  disconnectedCallback() {
    this.#abort?.abort();
    this.#controller?.abort();
  }

  async #changeLine(line, quantity) {
    this.setAttribute('aria-busy', 'true');

    try {
      const response = await fetch(this.dataset.cartChangeUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ line: Number(line), quantity })
      });

      if (!response.ok) throw new Error(`HTTP ${response.status}`);

      const cart = await response.json();
      for (const el of document.querySelectorAll('[data-cart-count]')) {
        el.textContent = cart.item_count;
        el.classList.toggle('ws-header__cart-count--empty', cart.item_count === 0);
      }
    } catch (error) {
      console.error('Cart change failed:', error);
    } finally {
      await this.#rerender();
      this.removeAttribute('aria-busy');
    }
  }

  async #rerender() {
    this.#controller?.abort();
    this.#controller = new AbortController();

    const url = new URL(window.location.href);
    url.searchParams.set('section_id', this.dataset.sectionId);

    try {
      const response = await fetch(url, { signal: this.#controller.signal });
      if (!response.ok) throw new Error(`HTTP ${response.status}`);

      const html = await response.text();
      const next = new DOMParser().parseFromString(html, 'text/html').querySelector('[data-cart-page]');
      const current = this.querySelector('[data-cart-page]');
      if (next && current) current.innerHTML = next.innerHTML;
    } catch (error) {
      if (error.name !== 'AbortError') console.error('Cart re-render failed:', error);
    }
  }
}

if (!customElements.get('ws-cart-page')) {
  customElements.define('ws-cart-page', WsCartPage);
}
